import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import DocSection, {
  DocSubsection,
  DocNote,
  DocImage,
} from "../components/DocSection";

const DataCharts = () => {
  const { t } = useTranslation();

  return (
    <DocSection id="data-charts" title={t("docs.dataCharts.title")}>
      {/* Exploring Datasets Section */}
      <DocSubsection id="datasets" title={t("docs.dataCharts.datasets")}>
        <p>{t("docs.dataCharts.datasetsDesc")}</p>

        <DocImage
          src="/docs/datasets-page.png"
          alt="Datasets Page with Category Filters"
          caption={t("docs.images.datasetsPage")}
        />

        <h4>{t("docs.dataCharts.categoriesTitle")}</h4>
        <p>{t("docs.dataCharts.categoriesDesc")}</p>
        <ul>
          <li>
            <strong>{t("docs.dataCharts.categoryCRC")}:</strong>{" "}
            {t("docs.dataCharts.categoryCRCDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.categoryScreening")}:</strong>{" "}
            {t("docs.dataCharts.categoryScreeningDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.categoryAirPollution")}:</strong>{" "}
            {t("docs.dataCharts.categoryAirPollutionDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.categoryRiskScores")}:</strong>{" "}
            {t("docs.dataCharts.categoryRiskScoresDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.categoryFusion")}:</strong>{" "}
            {t("docs.dataCharts.categoryFusionDesc")}
          </li>
        </ul>

        <h4>{t("docs.dataCharts.browsingTitle")}</h4>
        <p>{t("docs.dataCharts.browsingDesc")}</p>

        <DocNote type="info">
          {t("docs.dataCharts.metadataNote")}
        </DocNote>
      </DocSubsection>

      {/* Creating Charts Section */}
      <DocSubsection id="creating-charts" title={t("docs.dataCharts.creatingCharts")}>
        <p>{t("docs.dataCharts.creatingChartsDesc")}</p>

        <DocImage
          src="/docs/create-chart-page.png"
          alt="Create Chart Page"
          caption={t("docs.images.createChartPage")}
        />

        <h4>{t("docs.dataCharts.chartWorkflow")}</h4>
        <ol>
          <li>
            <strong>{t("docs.dataCharts.workflowStep1")}:</strong>{" "}
            {t("docs.dataCharts.workflowStep1Desc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.workflowStep2")}:</strong>{" "}
            {t("docs.dataCharts.workflowStep2Desc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.workflowStep3")}:</strong>{" "}
            {t("docs.dataCharts.workflowStep3Desc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.workflowStep4")}:</strong>{" "}
            {t("docs.dataCharts.workflowStep4Desc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.workflowStep5")}:</strong>{" "}
            {t("docs.dataCharts.workflowStep5Desc")}
          </li>
        </ol>

        <Link to="/create-chart" className="doc-link">
          {t("docs.dataCharts.createChartLink")} →
        </Link>
      </DocSubsection>

      {/* Pivot Tables Section */}
      <DocSubsection id="pivot-tables" title={t("docs.dataCharts.pivotTables")}>
        <p>{t("docs.dataCharts.pivotTablesDesc")}</p>

        <h4>{t("docs.dataCharts.pivotComponents")}</h4>
        <ul>
          <li>
            <strong>{t("docs.dataCharts.pivotRows")}:</strong>{" "}
            {t("docs.dataCharts.pivotRowsDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.pivotCols")}:</strong>{" "}
            {t("docs.dataCharts.pivotColsDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.pivotValues")}:</strong>{" "}
            {t("docs.dataCharts.pivotValuesDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.pivotFilters")}:</strong>{" "}
            {t("docs.dataCharts.pivotFiltersDesc")}
          </li>
        </ul>

        <DocImage
          src="/doc-img-1.png"
          alt="Pivot Table Variables"
          caption={t("docs.dataCharts.pivotImageCaption")}
        />

        <h4>{t("docs.dataCharts.aggregatorsTitle")}</h4>
        <p>{t("docs.dataCharts.aggregatorsDesc")}</p>
        <ul>
          <li>
            <strong>{t("docs.dataCharts.aggSum")}:</strong>{" "}
            {t("docs.dataCharts.aggSumDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.aggAverage")}:</strong>{" "}
            {t("docs.dataCharts.aggAverageDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.aggCount")}:</strong>{" "}
            {t("docs.dataCharts.aggCountDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.aggMin")}:</strong>{" "}
            {t("docs.dataCharts.aggMinDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.aggMax")}:</strong>{" "}
            {t("docs.dataCharts.aggMaxDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.aggMedian")}:</strong>{" "}
            {t("docs.dataCharts.aggMedianDesc")}
          </li>
        </ul>

        <DocImage
          src="/doc-img-2.png"
          alt="Values and Aggregators"
          caption={t("docs.dataCharts.aggregatorsImageCaption")}
        />
      </DocSubsection>

      {/* Chart Types Section */}
      <DocSubsection id="chart-types" title={t("docs.dataCharts.chartTypes")}>
        <p>{t("docs.dataCharts.chartTypesDesc")}</p>

        <DocImage
          src="/docs/chart-types-example.png"
          alt="Examples of Available Chart Types"
          caption={t("docs.images.chartTypesExample")}
        />

        <div className="chart-types-grid">
          <div className="chart-type-item">
            <h5>{t("docs.dataCharts.chartGroupedColumn")}</h5>
            <p>{t("docs.dataCharts.chartGroupedColumnDesc")}</p>
          </div>
          <div className="chart-type-item">
            <h5>{t("docs.dataCharts.chartStackedColumn")}</h5>
            <p>{t("docs.dataCharts.chartStackedColumnDesc")}</p>
          </div>
          <div className="chart-type-item">
            <h5>{t("docs.dataCharts.chartGroupedBar")}</h5>
            <p>{t("docs.dataCharts.chartGroupedBarDesc")}</p>
          </div>
          <div className="chart-type-item">
            <h5>{t("docs.dataCharts.chartStackedBar")}</h5>
            <p>{t("docs.dataCharts.chartStackedBarDesc")}</p>
          </div>
          <div className="chart-type-item">
            <h5>{t("docs.dataCharts.chartLine")}</h5>
            <p>{t("docs.dataCharts.chartLineDesc")}</p>
          </div>
          <div className="chart-type-item">
            <h5>{t("docs.dataCharts.chartArea")}</h5>
            <p>{t("docs.dataCharts.chartAreaDesc")}</p>
          </div>
          <div className="chart-type-item">
            <h5>{t("docs.dataCharts.chartScatter")}</h5>
            <p>{t("docs.dataCharts.chartScatterDesc")}</p>
          </div>
          <div className="chart-type-item">
            <h5>{t("docs.dataCharts.chartDot")}</h5>
            <p>{t("docs.dataCharts.chartDotDesc")}</p>
          </div>
          <div className="chart-type-item">
            <h5>{t("docs.dataCharts.chartPie")}</h5>
            <p>{t("docs.dataCharts.chartPieDesc")}</p>
          </div>
        </div>

        <h4>{t("docs.dataCharts.choosingChartTitle")}</h4>
        <ul>
          <li>
            <strong>{t("docs.dataCharts.chooseCompare")}:</strong>{" "}
            {t("docs.dataCharts.chooseCompareDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.chooseTrends")}:</strong>{" "}
            {t("docs.dataCharts.chooseTrendsDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.choosePartWhole")}:</strong>{" "}
            {t("docs.dataCharts.choosePartWholeDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.chooseDistribution")}:</strong>{" "}
            {t("docs.dataCharts.chooseDistributionDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.chooseGeographic")}:</strong>{" "}
            {t("docs.dataCharts.chooseGeographicDesc")}
          </li>
        </ul>
      </DocSubsection>

      {/* Geographic Maps Section */}
      <DocSubsection id="geographic-maps" title={t("docs.dataCharts.geographicMaps")}>
        <p>{t("docs.dataCharts.geographicMapsDesc")}</p>

        <DocImage
          src="/docs/geographic-map.png"
          alt="Geographic Map Visualization with Color Scale"
          caption={t("docs.images.geographicMap")}
        />

        <h4>{t("docs.dataCharts.nutsTitle")}</h4>
        <p>{t("docs.dataCharts.nutsDesc")}</p>
        <ul>
          <li>
            <strong>NUTS1:</strong> {t("docs.dataCharts.nuts1Desc")}
          </li>
          <li>
            <strong>NUTS2:</strong> {t("docs.dataCharts.nuts2Desc")}
          </li>
          <li>
            <strong>NUTS3:</strong> {t("docs.dataCharts.nuts3Desc")}
          </li>
        </ul>

        <h4>{t("docs.dataCharts.geoDatasets")}</h4>
        <ul>
          <li>
            <strong>{t("docs.dataCharts.geoScreening")}:</strong>{" "}
            {t("docs.dataCharts.geoScreeningDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.geoAirPollution")}:</strong>{" "}
            {t("docs.dataCharts.geoAirPollutionDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.geoRiskScores")}:</strong>{" "}
            {t("docs.dataCharts.geoRiskScoresDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.geoFusion")}:</strong>{" "}
            {t("docs.dataCharts.geoFusionDesc")}
          </li>
        </ul>

        <h4>{t("docs.dataCharts.mapInteraction")}</h4>
        <ul>
          <li>
            <strong>{t("docs.dataCharts.mapZoom")}:</strong>{" "}
            {t("docs.dataCharts.mapZoomDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.mapPan")}:</strong>{" "}
            {t("docs.dataCharts.mapPanDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.mapClick")}:</strong>{" "}
            {t("docs.dataCharts.mapClickDesc")}
          </li>
          <li>
            <strong>{t("docs.dataCharts.mapMetric")}:</strong>{" "}
            {t("docs.dataCharts.mapMetricDesc")}
          </li>
        </ul>

        <DocNote type="info">
          {t("docs.dataCharts.mapNote")}
        </DocNote>
      </DocSubsection>
    </DocSection>
  );
};

export default DataCharts;
